import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Store, Pencil, Trash2, MapPin, Building2, ExternalLink, Clock, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBranches() {
  const { getAuthHeaders } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ 
    business_name: '', 
    name: '', 
    address: '',
    google_maps_url: '',
    clabe: '',
    clabe_banco: '',
    clabe_beneficiario: '',
    opening_time: '09:00',
    closing_time: '20:00',
    saturday_open: true,
    saturday_opening_time: '09:00',
    saturday_closing_time: '20:00',
    sunday_open: false,
    sunday_opening_time: '10:00',
    sunday_closing_time: '18:00'
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      setBranches(response.data);
    } catch (error) {
      toast.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await axios.put(`${API}/branches/${editingBranch.id}`, formData, getAuthHeaders());
        toast.success('Sucursal actualizada');
      } else {
        await axios.post(`${API}/branches`, formData, getAuthHeaders());
        toast.success('Sucursal creada');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({ 
      business_name: '', 
      name: '', 
      address: '', 
      google_maps_url: '',
      clabe: '',
      clabe_banco: '',
      clabe_beneficiario: '',
      opening_time: '09:00', 
      closing_time: '20:00',
      saturday_open: true,
      saturday_opening_time: '09:00',
      saturday_closing_time: '20:00',
      sunday_open: false,
      sunday_opening_time: '10:00',
      sunday_closing_time: '18:00'
    });
    setEditingBranch(null);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({ 
      business_name: branch.business_name || '', 
      name: branch.name, 
      address: branch.address,
      google_maps_url: branch.google_maps_url || '',
      clabe: branch.clabe || '',
      clabe_banco: branch.clabe_banco || '',
      clabe_beneficiario: branch.clabe_beneficiario || '',
      opening_time: branch.opening_time || '09:00',
      closing_time: branch.closing_time || '20:00',
      saturday_open: branch.saturday_open !== false,
      saturday_opening_time: branch.saturday_opening_time || '09:00',
      saturday_closing_time: branch.saturday_closing_time || '20:00',
      sunday_open: branch.sunday_open || false,
      sunday_opening_time: branch.sunday_opening_time || '10:00',
      sunday_closing_time: branch.sunday_closing_time || '18:00'
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sucursal?')) return;
    try {
      await axios.delete(`${API}/branches/${id}`, getAuthHeaders());
      toast.success('Sucursal eliminada');
      fetchBranches();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Group branches by business name
  const groupedBranches = branches.reduce((acc, branch) => {
    const businessName = branch.business_name || 'Sin Negocio';
    if (!acc[businessName]) {
      acc[businessName] = [];
    }
    acc[businessName].push(branch);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-branches">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Sucursales</h1>
            <p className="text-zinc-500 mt-1">Gestiona las sucursales de tus negocios</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                onClick={openNewDialog}
                className="bg-gold text-black hover:bg-gold-dark"
                data-testid="add-branch-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Sucursal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  {editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Nombre del Negocio */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre del Negocio</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="The Holland Barbershop"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="branch-business-name-input"
                    required
                  />
                  <p className="text-xs text-zinc-500">Ej: The Holland Barbershop, Paulo Cisneros</p>
                </div>

                {/* Nombre de la Sucursal */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre de la Sucursal</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Sucursal Centro"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="branch-name-input"
                    required
                  />
                </div>

                {/* Dirección */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Dirección</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Av. Principal #123, Colonia Centro"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="branch-address-input"
                    required
                  />
                </div>

                {/* URL de Google Maps */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">URL de Google Maps</Label>
                  <Input
                    value={formData.google_maps_url}
                    onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                    placeholder="https://maps.google.com/..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="branch-maps-input"
                  />
                  <p className="text-xs text-zinc-500">El chatbot compartirá este enlace al agendar citas</p>
                </div>

                {/* Datos Bancarios para Transferencias */}
                <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <Label className="text-zinc-300 font-medium">Datos Bancarios para Transferencias</Label>
                  <p className="text-xs text-zinc-500 -mt-2">Se mostrarán al cliente cuando pague propina por transferencia</p>
                  
                  {/* Banco */}
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Banco</Label>
                    <Input
                      value={formData.clabe_banco}
                      onChange={(e) => setFormData({ ...formData, clabe_banco: e.target.value })}
                      placeholder="Ej: BBVA, Santander, Banorte..."
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="branch-banco-input"
                    />
                  </div>

                  {/* Nombre del Beneficiario */}
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Nombre del Beneficiario</Label>
                    <Input
                      value={formData.clabe_beneficiario}
                      onChange={(e) => setFormData({ ...formData, clabe_beneficiario: e.target.value })}
                      placeholder="Nombre como aparece en la cuenta"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="branch-beneficiario-input"
                    />
                  </div>

                  {/* CLABE Interbancaria */}
                  <div className="space-y-2">
                    <Label className="text-zinc-400">CLABE Interbancaria</Label>
                    <Input
                      value={formData.clabe}
                      onChange={(e) => setFormData({ ...formData, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) })}
                      placeholder="18 dígitos"
                      className="bg-zinc-800 border-zinc-700 text-white font-mono tracking-wider"
                      data-testid="branch-clabe-input"
                      maxLength={18}
                    />
                  </div>
                </div>

                {/* Horario de Lunes a Viernes */}
                <div className="space-y-2">
                  <Label className="text-zinc-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Horario Lunes a Viernes
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-xs text-zinc-500">Apertura</span>
                      <Input
                        type="time"
                        value={formData.opening_time}
                        onChange={(e) => setFormData({ ...formData, opening_time: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="branch-opening-time"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-zinc-500">Cierre</span>
                      <Input
                        type="time"
                        value={formData.closing_time}
                        onChange={(e) => setFormData({ ...formData, closing_time: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="branch-closing-time"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Horario de Sábado */}
                <div className="space-y-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Horario Sábado
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">
                        {formData.saturday_open ? 'Abierto' : 'Cerrado'}
                      </span>
                      <Switch
                        checked={formData.saturday_open}
                        onCheckedChange={(checked) => setFormData({ ...formData, saturday_open: checked })}
                        data-testid="branch-saturday-switch"
                      />
                    </div>
                  </div>
                  
                  {formData.saturday_open && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-1">
                        <span className="text-xs text-zinc-500">Apertura</span>
                        <Input
                          type="time"
                          value={formData.saturday_opening_time}
                          onChange={(e) => setFormData({ ...formData, saturday_opening_time: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="branch-saturday-opening"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-zinc-500">Cierre</span>
                        <Input
                          type="time"
                          value={formData.saturday_closing_time}
                          onChange={(e) => setFormData({ ...formData, saturday_closing_time: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="branch-saturday-closing"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Horario de Domingo */}
                <div className="space-y-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Horario Domingo
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">
                        {formData.sunday_open ? 'Abierto' : 'Cerrado'}
                      </span>
                      <Switch
                        checked={formData.sunday_open}
                        onCheckedChange={(checked) => setFormData({ ...formData, sunday_open: checked })}
                        data-testid="branch-sunday-switch"
                      />
                    </div>
                  </div>
                  
                  {formData.sunday_open && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-1">
                        <span className="text-xs text-zinc-500">Apertura</span>
                        <Input
                          type="time"
                          value={formData.sunday_opening_time}
                          onChange={(e) => setFormData({ ...formData, sunday_opening_time: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="branch-sunday-opening"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-zinc-500">Cierre</span>
                        <Input
                          type="time"
                          value={formData.sunday_closing_time}
                          onChange={(e) => setFormData({ ...formData, sunday_closing_time: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="branch-sunday-closing"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-zinc-500">El asistente de citas usará estos horarios para agendar</p>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                    data-testid="branch-submit-btn"
                  >
                    {editingBranch ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Branches Grouped by Business */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : branches.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <Store className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No hay sucursales registradas</p>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedBranches).map(([businessName, businessBranches]) => (
              <div key={businessName} className="space-y-4">
                {/* Business Header */}
                <div className="flex items-center gap-3 pb-2 border-b border-zinc-800">
                  <Building2 className="w-5 h-5 text-gold" />
                  <h2 className="font-heading text-xl text-white">{businessName}</h2>
                  <span className="text-sm text-zinc-500">({businessBranches.length} sucursales)</span>
                </div>

                {/* Branches Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {businessBranches.map((branch) => (
                    <Card 
                      key={branch.id} 
                      className="bg-zinc-900/50 border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
                      data-testid={`branch-card-${branch.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center">
                          <Store className="w-5 h-5 text-gold" />
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(branch)}
                            className="w-8 h-8 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                            data-testid={`edit-branch-${branch.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(branch.id)}
                            className="w-8 h-8 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-branch-${branch.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <h3 className="font-medium text-white mb-2">{branch.name}</h3>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2 text-zinc-400">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{branch.address}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>Lun-Vie: {branch.opening_time || '09:00'} - {branch.closing_time || '20:00'}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>
                            Sáb: {branch.saturday_open !== false
                              ? `${branch.saturday_opening_time || branch.opening_time || '09:00'} - ${branch.saturday_closing_time || branch.closing_time || '20:00'}` 
                              : 'Cerrado'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>
                            Dom: {branch.sunday_open 
                              ? `${branch.sunday_opening_time || '10:00'} - ${branch.sunday_closing_time || '18:00'}` 
                              : 'Cerrado'}
                          </span>
                        </div>
                        
                        {branch.google_maps_url && (
                          <a
                            href={branch.google_maps_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-gold hover:text-gold-dark transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>Ver en Google Maps</span>
                          </a>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
